import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Briefcase, Save, AlertCircle } from "lucide-react";
import { getBackend } from "../lib/backend";
import type { Brand } from "../types";

interface BrandModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (brand: Brand) => void;
}

export const BrandModal: React.FC<BrandModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState<Partial<Brand>>({
        name: "",
        description: "",
        product: "",
        tone: "",
        goal: "",
        is_active: 1
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const backend = await getBackend();
            const result = await backend.createBrand(formData);
            onSuccess(result as unknown as Brand);
            onClose();
        } catch (error) {
            console.error("Failed to create brand:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100]"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl z-[110] p-4"
                    >
                        <div className="bg-[#0f1115] border border-primary/30 rounded-[2.5rem] overflow-hidden shadow-[0_0_50px_rgba(var(--primary-rgb),0.15)]">
                            <div className="p-8 border-b border-white/5 bg-gradient-to-br from-primary/10 to-transparent">
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="p-3 bg-primary/20 rounded-2xl text-primary">
                                        <Briefcase size={24} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white">Create Brand Profile</h3>
                                </div>
                                <p className="text-white/60 text-sm">You need at least one brand profile to start generating comments.</p>
                            </div>

                            <form onSubmit={handleSave} className="p-8 space-y-5">
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Company / Brand Name</label>
                                    <input
                                        required
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g. Tekibo AI"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-5">
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Main Product</label>
                                        <input
                                            required
                                            value={formData.product}
                                            onChange={e => setFormData({ ...formData, product: e.target.value })}
                                            placeholder="e.g. Fitness App"
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Voice Tone</label>
                                        <input
                                            required
                                            value={formData.tone}
                                            onChange={e => setFormData({ ...formData, tone: e.target.value })}
                                            placeholder="e.g. Enthusiastic"
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">What do you do? (Description)</label>
                                    <textarea
                                        required
                                        rows={3}
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Describe your brand's core mission or service..."
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all resize-none"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-white/40 uppercase tracking-wider px-1">Primary Goal</label>
                                    <input
                                        required
                                        value={formData.goal}
                                        onChange={e => setFormData({ ...formData, goal: e.target.value })}
                                        placeholder="e.g. Professional engagement"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all"
                                    />
                                </div>

                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full py-5 bg-primary text-white font-bold rounded-[1.25rem] hover:bg-primary/90 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3 shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Save size={20} />
                                        )}
                                        <span>Create & Continue</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};
